import React from "react";
import { useIntl } from "react-intl";
import { useFormikContext } from "formik";
import { Space, Form, Row } from "@masterteam/mt-design-react";

import "./style.scss";

const { Field } = Form;

const BasicInformation = ({ lists, isReadOnly }) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const { values, setFieldValue } = useFormikContext();

  const getKPIs = () => {
    let kpis = [];

    if (values?.Attributes?.length) {
      values?.Attributes?.forEach((item) => {
        if (item?.KPIs?.length) kpis = [...kpis, ...item?.KPIs];
      });
    } else if (values?.StrategicObjective) {
      kpis = values?.StrategicObjective?.KPIs;
    }

    return kpis;
  };

  return (
    <Space direction="vertical" size="large" className="basic-information">
      <Row size="large" style={{ width: "300px" }}>
        <Field
          name="Code"
          label={intl.formatMessage({ id: "initiative.code" })}
          inputProps={{
            placeholder: intl.formatMessage({ id: "initiative.code" })
          }}
          disabled={true}
          hideErrorMessage
        />
      </Row>

      <Row size="large">
        <Field
          name="ArabicName"
          label={intl.formatMessage({ id: "initiative.arabic-name" })}
          inputProps={{
            placeholder: intl.formatMessage({ id: "initiative.arabic-name" })
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />
        <Field
          name="EnglishName"
          label={intl.formatMessage({ id: "initiative.english-name" })}
          inputProps={{
            placeholder: intl.formatMessage({ id: "initiative.english-name" })
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />
      </Row>

      <Row size="large">
        <Field
          type="textarea"
          name="ArabicDescription"
          label={intl.formatMessage({ id: "initiative.arabic-description" })}
          inputProps={{
            placeholder: intl.formatMessage({
              id: "initiative.arabic-description"
            })
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />
        <Field
          type="textarea"
          name="EnglishDescription"
          label={intl.formatMessage({ id: "initiative.english-description" })}
          inputProps={{
            placeholder: intl.formatMessage({
              id: "initiative.english-description"
            })
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />
      </Row>

      <Row size="large">
        <Field
          type="select"
          name="Department"
          label={intl.formatMessage({ id: "common.department" })}
          inputProps={{
            options: lists?.departments,
            onChange: (value) => {
              setFieldValue("Department", value);
              setFieldValue("OwnerModel", value.OwnerModel);
            },
            getOptionLabel: (el) => (isAr ? el.ArabicName : el.EnglishName),
            getOptionValue: (el) => el?.ID,
            placeholder: intl.formatMessage({
              id: "common.department"
            })
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />

        <Field
          type="select"
          label={intl.formatMessage({ id: "initiative.initiative-owner" })}
          name="OwnerModel"
          inputProps={{
            options: lists?.users,
            getOptionLabel: (el) => el.DisplayName,
            getOptionValue: (el) => el?.UserName,
            placeholder: intl.formatMessage({
              id: "initiative.initiative-owner"
            })
          }}
          disabled={true}
          hideErrorMessage
          required
        />
      </Row>

      <Row size="large">
        <Field
          type="select"
          label={intl.formatMessage({ id: "initiative.related-initiatives" })}
          name="Initiatives"
          inputProps={{
            options: lists?.initiatives,
            getOptionLabel: (el) => (isAr ? el.ArabicName : el.EnglishName),
            getOptionValue: (el) => el?.ID,
            placeholder: intl.formatMessage({
              id: "initiative.related-initiatives"
            }),
            isMulti: true
          }}
          disabled={isReadOnly}
          hideErrorMessage
        />

        <Field
          type="number"
          name="Duration"
          label={intl.formatMessage({ id: "initiative.duration" })}
          inputProps={{
            onKeyDown: (evt) => {
              if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                evt.preventDefault();
              }
            },
            placeholder: intl.formatMessage({
              id: "initiative.duration"
            })
          }}
          disabled={isReadOnly}
          hideErrorMessage
        />
      </Row>

      <Row size="large">
        <Field
          type="textarea"
          name="KeyResults"
          label={intl.formatMessage({ id: "initiative.key-result" })}
          inputProps={{
            placeholder: intl.formatMessage({ id: "initiative.key-result" })
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />

        <Field
          type="date"
          name="StartDate"
          label={intl.formatMessage({ id: "initiative.start-date" })}
          hideErrorMessage
          disabled={isReadOnly}
          required
        />
      </Row>

      <Row size="large">
        <Field
          type="select"
          name="Strategy"
          label={intl.formatMessage({
            id: "common.strategy"
          })}
          inputProps={{
            options: lists?.strategies,
            getOptionLabel: (el) => (isAr ? el.ArabicName : el.EnglishName),
            getOptionValue: (el) => el?.ID,
            menuPlacement: "top",
            placeholder: intl.formatMessage({
              id: "common.strategy"
            }),
            onChange: (e) => {
              setFieldValue("Strategy", e);
              setFieldValue("StrategicObjective", null);
              setFieldValue("Attributes", []);
              setFieldValue("KPIs", []);
            }
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />
        <Field
          type="select"
          label={intl.formatMessage({
            id: "common.strategic-objective"
          })}
          name="StrategicObjective"
          inputProps={{
            options: values.Strategy?.StrategicObjectives || [],
            getOptionLabel: (el) => (isAr ? el.ArabicName : el.EnglishName),
            getOptionValue: (el) => el?.ID,
            menuPlacement: "top",
            placeholder: intl.formatMessage({
              id: "common.strategic-objective"
            }),
            onChange: (e) => {
              setFieldValue("StrategicObjective", e);
              setFieldValue("Attributes", []);
              setFieldValue("KPIs", []);
            }
          }}
          disabled={isReadOnly}
          hideErrorMessage
          required
        />
      </Row>

      <Row size="large">
        <Field
          type="select"
          label={intl.formatMessage({
            id: "common.sub-strategic-objectives"
          })}
          name="Attributes"
          inputProps={{
            options: values.StrategicObjective?.StrategicSubObjective || [],
            getOptionLabel: (el) => (isAr ? el.ArabicName : el.EnglishName),
            getOptionValue: (el) => el?.ID,
            menuPlacement: "top",
            isClearable: true,
            placeholder: intl.formatMessage({
              id: "common.sub-strategic-objectives"
            }),
            onChange: (e) => {
              setFieldValue("Attributes", e);
              setFieldValue("KPIs", []);
            },
            isMulti: true
          }}
          disabled={isReadOnly}
          hideErrorMessage
        />

        <Field
          type="select"
          name="KPIs"
          label={intl.formatMessage({
            id: "common.kpis"
          })}
          inputProps={{
            options: getKPIs() || [],
            getOptionLabel: (el) => (isAr ? el.ArabicName : el.EnglishName),
            getOptionValue: (el) => el?.ID,
            menuPlacement: "top",
            isClearable: true,
            placeholder: intl.formatMessage({
              id: "common.kpis"
            }),
            isMulti: true
          }}
          disabled={isReadOnly}
          hideErrorMessage
        />
      </Row>
    </Space>
  );
};

export default BasicInformation;
